import React from 'react';

import classNames from 'classnames';

/**
 * A small pill badge with a leading dot, matching the IB-380 wireframe `.badge`.
 *
 * Variants map to InBudget's colour tokens (no new tokens):
 *   - success  → green   (Completed)
 *   - info     → blue    (Processing / in progress)
 *   - neutral  → charcoal (Pending / created) — the default
 *   - warning  → amber   (Needs attention, short of failed)
 *   - danger   → red     (Failed)
 *
 * Pass `variant` directly, or a report `status` key ('completed' | 'in_progress'
 * | 'created' | 'failed') which is mapped to the matching variant.
 *
 * Pass `icon` (a small SVG node using `currentColor`) to render a leading icon in
 * place of the dot — e.g. a download or on-screen glyph on a format tag.
 *
 * `size` scales the pill: 'sm' (default) or 'md' (one step larger — bigger text,
 * padding, gap and leading glyph).
 */
const VARIANT_CLASSES = {
    success: 'tw-bg-green-25 tw-text-green-600',
    info: 'tw-bg-blue-10 tw-text-blue-600',
    neutral: 'tw-bg-charcoal-15 tw-text-charcoal-75',
    warning: 'tw-bg-amber-100 tw-text-amber-700',
    danger: 'tw-bg-red-25 tw-text-red-600',
};

const STATUS_VARIANTS = {
    completed: 'success',
    in_progress: 'info',
    created: 'neutral',
    failed: 'danger',
};

const SIZE_CLASSES = {
    sm: { pill: 'tw-text-2xs tw-gap-1.5 tw-py-0.5 tw-px-1.5', icon: 'tw-w-3 tw-h-3', dot: 'tw-w-1.5 tw-h-1.5' },
    md: { pill: 'tw-text-xs tw-gap-2 tw-py-1 tw-px-2', icon: 'tw-w-3.5 tw-h-3.5', dot: 'tw-w-2 tw-h-2' },
};

export default function Badge({ variant, status, icon, size = 'sm', children, className }) {
    const resolvedVariant = variant || STATUS_VARIANTS[status] || 'neutral';
    const sizeClasses = SIZE_CLASSES[size] || SIZE_CLASSES.sm;

    return (
        <span
            className={classNames(
                'tw-inline-flex tw-items-center tw-font-medium tw-rounded-md',
                sizeClasses.pill,
                VARIANT_CLASSES[resolvedVariant] || VARIANT_CLASSES.neutral,
                className,
            )}
        >
            {icon ? (
                <span className={classNames('tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center', sizeClasses.icon)} aria-hidden="true">
                    {icon}
                </span>
            ) : (
                <span className={classNames('tw-rounded-full tw-bg-current', sizeClasses.dot)} aria-hidden="true" />
            )}
            {children}
        </span>
    );
}
